import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import React from 'react';
import Layout from '@/components/common/Layout';
import ScrollToTop from '@/components/common/ScrollToTop';
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import MetanetDesktop from "./pages/MetanetDesktop";
import OverlayHostingServices from "./pages/OverlayHostingServices";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Provenance from "./pages/Provenance";
import ImmutableLogCapture from "./pages/ImmutableLogCapture";
import SecureTeleFormsGenerator from "./pages/SecureTeleFormsGenerator";
import SmartContractDevelopment from "./pages/SmartContractDevelopment";
import ProvablyFairGaming from "./pages/ProvablyFairGaming";

// Import new page components
import WalletStorageManagement from './pages/WalletStorageManagement';
import ChainBackedStorageServices from './pages/ChainBackedStorageServices';
import IdentityVerificationServices from './pages/IdentityVerificationServices';
import OnboardingOffboarding from './pages/OnboardingOffboarding';
import HostedP2PMessageBox from './pages/HostedP2PMessageBox';
import DocumentSigningSystem from './pages/DocumentSigningSystem';
import MetanetEngineeringCurriculum from './pages/MetanetEngineeringCurriculum';
import BlockchainTrainingEnterpriseEngineers from './pages/BlockchainTrainingEnterpriseEngineers';
import FinancialTradingTokenization from './pages/FinancialTradingTokenization';
import ArchivalRecordkeeping from './pages/ArchivalRecordkeeping';
import SkilledIndustries from './pages/SkilledIndustries';
import ApiPlatformMonetization from './pages/ApiPlatformMonetization';
import BlockchainIntegrationConsultancy from './pages/BlockchainIntegrationConsultancy';

const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Layout>
              <Routes>
                {/* Core Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />

                {/* Existing Product Routes */}
                <Route path="/metanet-desktop" element={<MetanetDesktop />} />
                <Route path="/overlay-hosting-services" element={<OverlayHostingServices />} />
                {/* Note: ImmutableLogCapture component exists, route updated */}
                <Route path="/immutable-logcapture" element={<ImmutableLogCapture />} />
                {/* Note: SecureTeleFormsGenerator component exists, route updated */}
                <Route path="/secure-teleforms-generator" element={<SecureTeleFormsGenerator />} />

                {/* Existing Solution Routes */}
                {/* Note: Provenance component exists */}
                <Route path="/provenance" element={<Provenance />} />
                {/* Note: SmartContractDevelopment component exists */}
                <Route path="/smart-contract-development" element={<SmartContractDevelopment />} />
                {/* Note: ProvablyFairGaming component exists, route updated */}
                <Route path="/provably-fair-gaming-architecture" element={<ProvablyFairGaming />} />

                {/* Routes for New Products */}
                <Route path="/wallet-storage-management" element={<WalletStorageManagement />} />
                <Route path="/chain-backed-storage-services" element={<ChainBackedStorageServices />} />
                <Route path="/identity-verification-services" element={<IdentityVerificationServices />} />
                <Route path="/onboarding-offboarding" element={<OnboardingOffboarding />} />
                <Route path="/hosted-p2p-message-box" element={<HostedP2PMessageBox />} />
                <Route path="/document-signing-system" element={<DocumentSigningSystem />} />
                <Route path="/metanet-engineering-curriculum" element={<MetanetEngineeringCurriculum />} />

                {/* Routes for New Solutions */}
                <Route path="/blockchain-training-enterprise-engineers" element={<BlockchainTrainingEnterpriseEngineers />} />
                <Route path="/financial-trading-tokenization" element={<FinancialTradingTokenization />} />
                <Route path="/archival-recordkeeping" element={<ArchivalRecordkeeping />} />
                <Route path="/skilled-industries" element={<SkilledIndustries />} />
                <Route path="/api-platform-monetization" element={<ApiPlatformMonetization />} />
                <Route path="/blockchain-integration-consultancy" element={<BlockchainIntegrationConsultancy />} />

                {/* Catch-all Not Found Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
